import { useEffect } from "react";
import { SiweActions, useAuthStore } from "@shared/src/stores/useAuthStore";
import { IoCopy } from "react-icons/io5";
import { Spinner } from "@shared/src/components/common/misc/Spinner";
import { QRCodeSVG } from "qrcode.react";
import { IoIosClose } from "react-icons/io";
import { Button } from "@shared/src/components/ui/button";
import { Connector } from "wagmi";
import { copyToClipboard } from "@shared/src/utils/helpers";
import useAuth from "@shared/src/hooks/auth/useAuth";
import { ixToast } from "@shared/src/utils/ixToast";
import { motion } from "framer-motion";
import { retryWalletConnection } from "@shared/src/hooks/auth/walletManager";

interface WalletConnectionProps {
  walletIcon: string;
  walletName: string;
  type: "siwe" | "wallet" | "social";
}

// Internal Components for each state
function InitialState({}: {
  //TODO: ADD WALLET ICON
  walletName: string;
  walletIcon: string;
}) {
  return (
    <div className="flex items-center justify-center w-full h-[240px]">
      <Spinner className="text-text-primary" size={40} />
    </div>
  );
}

export function AwaitingConfirmation({ walletName, walletIcon, type }: Omit<WalletConnectionProps, "wallet">) {
  const { walletStatus } = useAuthStore();

  return (
    <>
      <div className="relative rounded-xl p-4">
        {/* Base border */}
        <div className="absolute inset-0 rounded-xl border-2 border-border-tertiary" />

        {/* Animated border - only animate when not in error state */}
        {walletStatus !== "error" && (
          <motion.div
            className="absolute inset-0 rounded-xl border-2 border-border-focus"
            style={{
              clipPath: "inset(0 0 calc(100% - 2px) 0)",
            }}
            animate={{
              clipPath: [
                "inset(0 0 calc(100% - 2px) 0)", // Top
                "inset(0 0 0 calc(100% - 2px))", // Right
                "inset(calc(100% - 2px) 0 0 0)", // Bottom
                "inset(0 calc(100% - 2px) 0 0)", // Left
                "inset(0 0 calc(100% - 2px) 0)", // Back to top
              ],
            }}
            transition={{
              duration: 2,
              ease: "linear",
              repeat: Infinity,
            }}
          />
        )}

        <div className="relative z-10 bg-surfaces-bg-secondary rounded-lg flex items-center justify-center">
          <img src={walletIcon} alt={walletName} width={80} height={80} />
        </div>
      </div>
      <p className="text-text-primary text-large font-extrabold mt-4 text-center">
        {type === "siwe" ? "Sign Message" : "Awaiting Confirmation..."}
      </p>
      <p className="text-text-tertiary text-center">
        {type === "siwe"
          ? `Please sign the message in your wallet to verify it`
          : `Please accept the connection request`}
      </p>
    </>
  );
}

export function SiweRequest({
  name,
  connectorIcon,
  actions,
}: {
  name: string;
  connectorIcon: string;
  actions: SiweActions;
}) {
  const { walletStatus } = useAuthStore();

  return (
    <div className="flex flex-col items-center justify-center w-full h-full space-y-6">
      <div className="relative rounded-full p-4 bg-surfaces-bg-secondary">
        <div className="relative z-10 bg-surfaces-bg-secondary rounded-full flex items-center justify-center">
          <img src={connectorIcon} alt={name} width={80} height={80} />
        </div>
      </div>
      <p className="text-text-primary text-large font-extrabold text-center">Sign In</p>
      <p className="text-text-tertiary text-center">Please sign the message request in your wallet to continue</p>
      <div className="flex items-center justify-center gap-4 w-full">
        <Button onClick={() => actions.reject()} variant="tertiary" size="cta" radius="full" width="full">
          Disconnect
        </Button>
        <Button onClick={() => actions.resolve()} variant="primary" size="cta" radius="full" width="full">
          Sign In
        </Button>
      </div>
    </div>
  );
}

function ErrorState({ walletName }: { walletName: string; walletIcon: string }) {
  const { retryWalletConnection } = useAuth();

  const handleRetry = async () => {
    //TODO: mmake this work
    try {
      // Check if it's WalletConnect
      if (walletName === "WalletConnect") {
        //TODO: @Jovells
        useAuthStore.getState().closeAndResetAuthModal();
      } else {
        retryWalletConnection(walletName);
        // useAuthStore.getState().setSelectedView(walletName);
        // useAuthStore.getState().setWalletStatus("initial", "Wallet");
        // // await retryWalletConnection(walletName);
      }
    } catch (error) {
      ixToast.error("Retry failed. Please try again.");
    }
  };

  return (
    //TODO: @Jovells FIX
    useAuthStore.getState().closeAndResetAuthModal(),
    (
      <div className="flex flex-col items-center gap-8 w-full">
        <div className="bg-surfaces-bg-secondary-darker rounded-full p-4">
          <IoIosClose size={28} className="text-icons-icon-error" />
        </div>
        <div className="text-center">
          <p className="text-text-primary text-h6 font-semibold mb-2">Connection failed. Please try again.</p>
        </div>
        <div className="flex justify-center">
          <Button onClick={handleRetry} variant="tertiary" size="lg" radius="full" width="full" className="px-16">
            Retry
          </Button>
        </div>
      </div>
    )
  );
}

// Container Component
function WalletStateContainer({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center w-full min-h-[300px]">
      <div className="flex-1 flex flex-col items-center gap-4">{children}</div>
    </div>
  );
}

// Main Component
export function WalletConnection({ walletIcon, walletName }: WalletConnectionProps) {
  const { walletStatus: status, connectionLink, siweActions } = useAuthStore();
  const handleCopyLink = () => {
    if (connectionLink) {
      copyToClipboard(connectionLink);
      ixToast.success("Connection link copied to clipboard");
    }
  };

  switch (status) {
    case "initial":
      return (
        <WalletStateContainer>
          <InitialState walletName={walletName} walletIcon={walletIcon as string} />
        </WalletStateContainer>
      );

    case "awaiting_confirmation":
      return (
        <WalletStateContainer>
          <AwaitingConfirmation walletName={walletName} walletIcon={walletIcon as string} type="wallet" />
        </WalletStateContainer>
      );

    case "siwe_confirmation":
      return (
        <WalletStateContainer>
          <AwaitingConfirmation walletName={walletName} walletIcon={walletIcon as string} type="siwe" />
        </WalletStateContainer>
      );
    case "siwe_request":
      if (!siweActions) {
        throw "No actions provided for siwe request";
      }
      return (
        <WalletStateContainer>
          <SiweRequest name={walletName} connectorIcon={walletIcon as string} actions={siweActions} />
        </WalletStateContainer>
      );
    case "error":
      return (
        <WalletStateContainer>
          <ErrorState walletIcon={walletIcon} walletName={walletName} />
        </WalletStateContainer>
      );

    default:
      return null;
  }
}
